import { computed, observable } from 'mobx';
import { UserModel } from '../user/model';
import { AuthModel } from '../auth/model';
import { isBrandSponsored } from '../../lib/helpers';
import { PricingModel } from '../pricing/model';
import { BasketModel } from '../basket/model';
import { ChannelSchema, TrackSchema } from '../../types/schema';
import { formatCurrency } from '../../lib/currency';

export class ModalModel {
  constructor(
    private user: UserModel,
    private auth: AuthModel,
    private pricing: PricingModel,
    private basket: BasketModel,
  ) {}

  @computed get userId(): string {
    return this.user.user ? this.user.user.identity : null;
  }

  @computed get showAny(): boolean {
    return this.downloadShow || this.attributionShow || this.addChannelShow || this.selectChannelShow;
  }

  @observable downloadShow = false;
  @observable downloadAgree = false;
  @observable downloadURL: string = null;
  @observable downloadError: string = null;
  @observable downloadLoading = false;
  @observable downloadAttribution: string;
  @observable downloadLimitReached = false;
  @observable licenseIdentity = '';

  @computed get downloadModal() {
    return {
      show: this.downloadShow,
      agree: this.downloadAgree,
      loading: this.downloadLoading,
      error: this.downloadError,
      downloadURL: this.downloadURL,
      attribution: this.downloadAttribution,
      downloadLimitReached: this.downloadLimitReached,
      licenseIdentity: this.licenseIdentity,
    };
  }

  @observable attributionShow = false;
  @observable attributionLoading = false;
  @observable attributionAttribution: string = null;

  @computed get attributionModal() {
    return {
      show: this.attributionShow,
      loading: this.attributionLoading,
      attribution: this.attributionAttribution,
    };
  }

  @observable addChannelShow = false;
  @observable addChannelResults: Array<ChannelSchema> = null;
  @observable addChannelLoading = false;
  @observable addChannelLinking = false;
  @observable addChannelSelection: ChannelSchema = null;
  @observable addChannelError: string = null;

  @computed get addChannel() {
    return {
      show: this.addChannelShow,
      results: this.addChannelResults,
      loading: this.addChannelLoading,
      linking: this.addChannelLinking,
      selection: this.addChannelSelection,
      error: this.addChannelError,
      youtubeChannelLoginURL: this.auth.youtubeChannelLoginURL,
    };
  }

  @observable selectChannelShow = false;
  @observable selectChannelShowV2 = false;
  @observable selectChannelLoading = false;
  @observable selectChannelChannels: Array<ChannelSchema> = [];
  @observable selectChannelSelecting = false;
  @observable selectChannelActivating: ChannelSchema = null;

  @computed
  get selectChannel() {
    return {
      show: this.selectChannelShow,
      loading: this.selectChannelLoading,
      selecting: this.selectChannelSelecting,
      channels: this.selectChannelChannels,
      channel: this.user.channel,
      activating: this.selectChannelActivating,
    };
  }

  @computed
  get selectChannelV2() {
    return {
      show: this.selectChannelShowV2,
      loading: this.selectChannelLoading,
      selecting: this.selectChannelSelecting,
      channels: this.selectChannelChannels,
      channel: this.user.channel,
      activating: this.selectChannelActivating,
      addUrl: this.auth.youtubeChannelLoginURL,
    };
  }

  @computed
  get migrationChannel() {
    return {
      loading:
        !this.user ||
        this.user.channel === null ||
        this.user.channels.findIndex((channel) => channel.id === this.user.channel.id) === -1,
      selecting: this.selectChannelSelecting,
      channels: this.user.channels,
      channel: this.user.channel,
      addUrl: this.auth.youtubeChannelLoginURL,
    };
  }

  @computed get mustReAuthShow(): boolean {
    return this.auth.mustReAuth;
  }

  @computed
  get mustReAuth() {
    return {
      show: this.mustReAuthShow,
    };
  }

  @observable restrictedShow: boolean;

  @computed get restricted() {
    return {
      show: this.restrictedShow,
    };
  }

  @observable basketRestrictedShow: boolean;

  @computed get basketRestricted() {
    return {
      show: this.basketRestrictedShow,
      basket: this.basket,
    };
  }

  @observable licenseLoading = false;
  @observable licenseShow: boolean;
  @observable licenseTrack: TrackSchema;
  @computed get licenseRates(): Array<{
    id: string;
    label: string;
    value: string;
    available: boolean;
  }> {
    return this.pricing.rates;
  }

  @computed get license() {
    return {
      show: this.licenseShow,
      track: this.licenseTrack,
      loading: this.licenseLoading,
      firstFree:
        (this.basket.firstTrackIsFree && !this.basket.basketIncludesStandardTrack) ||
        !(this.user.channel && this.user.channelBrandSponsored),
      showPrices: !!this.user.channel && !!this.user.channelBrandSponsored,
      rates: this.licenseRates.reduce(
        (rates, rate) => [
          ...rates,
          {
            ...rate,
            value:
              !this.user.channel || !this.user.channelBrandSponsored
                ? rate.value
                : isBrandSponsored(rate.id)
                ? formatCurrency(
                    this.user.channelBrandSponsored.ratecard.value,
                    this.user.channelBrandSponsored.ratecard.currency,
                  )
                : formatCurrency(this.user.channel.ratecard.value, this.user.channel.ratecard.currency),
            available: isBrandSponsored(rate.id)
              ? this.licenseTrack
                ? this.licenseTrack.brand_sponsored
                : false
              : true,
          },
        ],
        [],
      ),
    };
  }

  @observable deleteAccountLoading = false;
  @observable deleteAccountShow = false;
  @observable deleteAccountPageBody: any = [];
  @observable deleteAccountError: string;
  @observable deleteAccountSaving = false;

  @computed get deleteAccount() {
    return {
      show: this.user.user && this.deleteAccountShow,
      loading: this.deleteAccountLoading,
      page_body: this.deleteAccountPageBody,
      error: this.deleteAccountError,
      saving: this.deleteAccountSaving,
    };
  }

  @observable downloadAccountDataLoading = false;
  @observable downloadAccountDataSaving = false;
  @observable downloadAccountDataShow = false;
  @observable downloadAccountDataSuccess = false;
  @observable downloadAccountDataError: string;

  @computed get downloadAccountData() {
    return {
      show: this.user.user && this.downloadAccountDataShow,
      loading: this.downloadAccountDataLoading,
      saving: this.downloadAccountDataSaving,
      success: this.downloadAccountDataSuccess,
      error: this.downloadAccountDataError,
    };
  }
}
